import { Notification } from "./store/definitions";

/*
 * const exampleNotification: Notification = {
 *     id: "ExampleNotification",
 *     dismissible: true,
 *     message: "Example notification",
 *     type: "neutral"
 * };
 */

const shortenFileName = (string: string, maxChar = 50) => {
    const [, filename, fileExtension] = string.match(/^(.+)(\.[\S]*)$/) || [];

    return `${filename.substr(0, maxChar)}[...]${fileExtension || ""}`;
};

const fileAttachmentAlreadyAttachedNotification = ({ fileName }: { fileName: string }): Notification => ({
    id: `FileAttachmentAlreadyAttachedNotification_${Math.random()}`,
    dismissible: true,
    message: `${shortenFileName(fileName)} já está anexado.`,
    type: "error",
    timeout: 10000
});

const fileAttachmentInvalidSizeNotification = ({
    fileName,
    maxFileSize
}: {
    fileName: string;
    maxFileSize: string;
}): Notification => ({
    id: `FileAttachmentInvalidSizeNotification_${Math.random()}`,
    dismissible: true,
    message: `${shortenFileName(
        fileName
    )} não pode ser anexado porque o arquivo é muito grande. O tamanho máximo é ${maxFileSize}`,
    type: "error",
    timeout: 10000
});

const fileAttachmentInvalidTypeNotification = ({ fileName }: { fileName: string }): Notification => ({
    id: `FileAttachmentInvalidTypeNotification_${Math.random()}`,
    dismissible: true,
    message: `${shortenFileName(
        fileName
    )} não pode ser anexado porque esse tipo de arquivo não é suportado. Tente outro arquivo.`,
    type: "error",
    timeout: 10000
});

/*
 * Recusa por QUANTIDADE. Existe porque a Twilio aceita no máximo 25 mídias por mensagem e a recusa
 * dela chegava como erro técnico ("Too many media attachments (51 > 25)"), sem dizer nada a quem
 * está do outro lado. Aqui o cliente descobre no momento de anexar, que é quando dá para agir.
 */
const fileAttachmentTooManyNotification = ({ maxFiles }: { maxFiles: number }): Notification => ({
    id: `FileAttachmentTooManyNotification_${Math.random()}`,
    dismissible: true,
    message: `Você pode enviar no máximo ${maxFiles} arquivos por mensagem. Envie estes primeiro e depois anexe o restante.`,
    type: "error",
    timeout: 10000
});

const fileDownloadInvalidSizeNotification = ({
    fileName,
    maxFileSize
}: {
    fileName: string;
    maxFileSize: string;
}): Notification => ({
    id: `FileDownloadInvalidSizeNotification_${Math.random()}`,
    dismissible: true,
    message: `${shortenFileName(
        fileName
    )} não pode ser baixado porque o arquivo é muito grande. O tamanho máximo é ${maxFileSize}`,
    type: "error",
    timeout: 10000
});

const fileDownloadInvalidTypeNotification = ({ fileName }: { fileName: string }): Notification => ({
    id: `FileDownloadInvalidTypeNotification_${Math.random()}`,
    dismissible: true,
    message: `${shortenFileName(fileName)} não pode ser baixado porque esse tipo de arquivo não é suportado.`,
    type: "error",
    timeout: 10000
});

const noConnectionNotification = (): Notification => ({
    id: "NoConnectionNotification",
    dismissible: true,
    message: "Conexão perdida. Tentando reconectar.",
    type: "warning"
});

/*
 * Falha TERMINAL de reconexão: o widget tentou sozinho e desistiu. Diferente da
 * `noConnectionNotification` (que promete "tentando reconectar"), esta encerra a promessa — sem
 * ela, o aviso de "tentando" ficaria na tela para sempre, mentindo que ainda há tentativa em curso.
 * Não é `dismissible: false`: o cliente tem que poder tirar o aviso da frente e seguir lendo o histórico.
 */
const failedToReconnectNotification = (): Notification => ({
    id: "FailedToReconnectNotification",
    dismissible: true,
    message: "Não foi possível reconectar. Recarregue a página para continuar o atendimento.",
    type: "error"
});

const failedToInitSessionNotification = (error?: string): Notification => ({
    id: `FailedToInitSessionNotification`,
    dismissible: true,
    /*
     * SEM `error` = mensagem genérica, e é o caminho preferido para falha de conexão: o motivo
     * técnico (texto do SDK do Twilio, em inglês e às vezes com detalhe interno de infra) não vai
     * pra tela do apostador — vai pro `reportClientError`, que é onde a operação consegue ler.
     */
    message: error
        ? `Algo deu errado. ${error}. Tente novamente mais tarde.`
        : "Não foi possível iniciar o atendimento. Tente novamente em alguns instantes.",
    type: "error"
});

/*
 * Falha ao enviar (texto ou mídia). O erro era engolido: o cliente ficava com o
 * composer travado, sem explicação, achando que a mensagem tinha ido.
 * Id único por ocorrência — duas tentativas seguidas mostram dois avisos.
 */
/*
 * Conversa encerrada no meio do caminho. Vem do 400 do Twilio (enviar para conversa `closed`) —
 * causa-raiz reproduzida em 2026-08-28. NÃO pode reusar a `failedToSendMessageNotification`: dizer
 * "verifique sua conexão" manda o apostador investigar a internet dele quando o problema é que o
 * atendimento terminou. O `timeout` é maior porque o cliente precisa LER e entender o próximo passo.
 */
const conversationEndedNotification = (): Notification => ({
    id: "ConversationEndedNotification",
    dismissible: true,
    message: "Este atendimento foi encerrado. Inicie uma nova conversa para continuar.",
    type: "warning",
    timeout: 15000
});

const failedToSendMessageNotification = (): Notification => ({
    id: `FailedToSendMessageNotification_${Math.random()}`,
    dismissible: true,
    message: "Não foi possível enviar. Verifique sua conexão e tente novamente.",
    type: "error",
    timeout: 10000
});

const invalidCpfNotification = (): Notification => ({
    id: "InvalidCpfNotification",
    dismissible: true,
    message: "CPF inválido. Verifique os números digitados.",
    type: "error",
    timeout: 10000
});

const invalidEmailNotification = (): Notification => ({
    id: "InvalidEmailNotification",
    dismissible: true,
    message: "E-mail inválido. Verifique o endereço digitado.",
    type: "error",
    timeout: 10000
});

export const notifications = {
    fileAttachmentAlreadyAttachedNotification,
    fileAttachmentInvalidSizeNotification,
    fileAttachmentInvalidTypeNotification,
    fileAttachmentTooManyNotification,
    fileDownloadInvalidSizeNotification,
    fileDownloadInvalidTypeNotification,
    noConnectionNotification,
    failedToInitSessionNotification,
    failedToReconnectNotification,
    conversationEndedNotification,
    failedToSendMessageNotification,
    invalidCpfNotification,
    invalidEmailNotification
};
